// MuseumInfoSection.js - Component for editing business information
import React, { useState, useEffect, useRef } from 'react';
import { Check, AlertCircle, MapPin, Phone, Mail, Globe, Coffee, ShoppingBag, Wifi, Package, Link, Building2, ImagePlus, Loader2 } from 'lucide-react';
import { useAccessControl } from '../../../contexts/AccessControlContext';
import { useImageUpload } from '../../hooks/useImageUpload';
import { generateSafeFolderName, sanitizeFilename } from './filenameSanitizer';
// getAllMuseums already uses mobileDb internally
import { getAllMuseums } from '../../MuseumObjects/utils/museumsDatabase';
import CollapsibleSection from '../../ui/CollapsibleSection';

export const MuseumInfoSection = ({ itinerary, setItinerary }) => {
  useAccessControl();
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const logoInputRef = useRef(null);
  const { uploadImage, isUploading } = useImageUpload();

  // Available museums for linking
  const [availableMuseums, setAvailableMuseums] = useState([]);
  const [museumsLoading, setMuseumsLoading] = useState(false);

  // Load available museums on mount
  useEffect(() => {
    const loadMuseums = async () => {
      setMuseumsLoading(true);
      try {
        const museums = await getAllMuseums();
        setAvailableMuseums(museums);
      } catch (err) {
        console.error('Failed to load museums:', err);
      } finally {
        setMuseumsLoading(false);
      }
    };
    loadMuseums();
  }, [itinerary.metadata.museum_info, setItinerary]);

  // Initialize museum_info if it doesn't exist
  useEffect(() => {
    if (!itinerary.metadata.museum_info) {
      setItinerary(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          museum_infos: false,
          museum_info: {
            venue_type: '',
            description: '',
            opening_hours: '',
            admission: '',
            address: '',
            latitude: '',
            longitude: '',
            accessibility: '',
            wifi: '',
            gift_shop: '',
            cafe: '',
            phone: '',
            email: '',
            website: '',
            logoUrl: ''
          }
        }
      }));
    }
  }, [itinerary.metadata.museum_info, setItinerary]);

  useEffect(() => {
    const museumLogoUrl = itinerary.metadata?.museum_info?.logoUrl?.trim();
    const venueLogoUrl = itinerary.metadata?.venue_page?.logoUrl?.trim();

    if (!museumLogoUrl || venueLogoUrl) {
      return;
    }

    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        venue_page: {
          ...(prev.metadata.venue_page || {}),
          entityType: 'business',
          displayName: prev.metadata?.venue_page?.displayName || prev.metadata?.name || '',
          logoUrl: museumLogoUrl
        }
      }
    }));
  }, [itinerary.metadata?.museum_info?.logoUrl, itinerary.metadata?.venue_page?.logoUrl, setItinerary]);

  const museumInfo = itinerary.metadata.museum_info || {};
  const museumInfosEnabled = itinerary.metadata.museum_infos || false;

  const syncBusinessPageMetadata = (metadata, field, value) => {
    const currentVenuePage = metadata.venue_page || {};
    const currentVenueInfo = currentVenuePage.info || {};

    const infoFieldMap = {
      opening_hours: 'opening_hours',
      admission: 'admission',
      address: 'address',
      accessibility: 'accessibility',
      wifi: 'wifi',
      gift_shop: 'gift_shop',
      cafe: 'cafe',
      phone: 'phone',
      email: 'email',
      website: 'website'
    };

    const nextVenuePage = {
      ...currentVenuePage,
      entityType: 'business',
      displayName: currentVenuePage.displayName || metadata.name || '',
      logoUrl: field === 'logoUrl' ? value : (currentVenuePage.logoUrl || ''),
      info: currentVenueInfo
    };

    if (field === 'description') {
      nextVenuePage.welcomeTitle = currentVenuePage.welcomeTitle || (metadata.name ? `Welcome to ${metadata.name}` : 'Welcome');
    }

    if (infoFieldMap[field]) {
      nextVenuePage.info = {
        ...currentVenueInfo,
        [infoFieldMap[field]]: value
      };
    }

    return nextVenuePage;
  };

  const handleFieldChange = (field, value) => {
    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        museum_info: {
          ...prev.metadata.museum_info,
          [field]: value
        },
        venue_page: syncBusinessPageMetadata(prev.metadata, field, value)
      }
    }));
  };

  const handleToggleMuseumTab = (enabled) => {
    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        museum_infos: enabled,
        venue_page: {
          ...(prev.metadata.venue_page || {}),
          entityType: 'business',
          displayName: prev.metadata?.venue_page?.displayName || prev.metadata?.name || ''
        }
      }
    }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setStatus(null);
      setMessage('');

      const safeCountry = String(itinerary.metadata?.country || 'unknown')
        .trim()
        .toLowerCase()
        .replace(/[^\w]/g, '_')
        .replace(/_+/g, '_');
      const guideFolderName = itinerary.metadata?.filename
        ? sanitizeFilename(itinerary.metadata.filename, { maxLength: Infinity, preserveHyphens: true }).replace(/\.json$/, '')
        : generateSafeFolderName(itinerary.metadata || {});
      const safeLogoFilename = sanitizeFilename(file.name, { maxLength: Infinity, preserveHyphens: true });
      const uploadMetadata = {
        country: safeCountry,
        itineraryName: guideFolderName,
        filename: `${guideFolderName}.json`
      };

      const logoUrl = await uploadImage(file, uploadMetadata, {
        maxSizeKB: 1500,
        storagePath: `special_itineraries/${safeCountry}/${guideFolderName}/images/${safeLogoFilename}`
      });

      handleFieldChange('logoUrl', logoUrl);
      setStatus('success');
      setMessage('Business logo uploaded successfully.');
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Failed to upload business logo.');
    } finally {
      event.target.value = '';
    }
  };

  // Handle linking/unlinking museum objects catalog
  const handleLinkedMuseumChange = (museumId) => {
    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        linkedMuseumId: museumId || null
      }
    }));
  };

  const linkedMuseumId = itinerary.metadata?.linkedMuseumId || '';
  const linkedMuseum = availableMuseums.find(m => m.id === linkedMuseumId);

  const validateFields = () => {
    if (!museumInfosEnabled) {
      setStatus('success');
      setMessage('Business page is disabled. No validation required.');
      return true;
    }

    // If enabled, at least description should be provided
    if (!museumInfo.description || museumInfo.description.trim() === '') {
      setStatus('error');
      setMessage('Please provide at least a business description when the business page is enabled.');
      return false;
    }

    setStatus('success');
    setMessage('Business information validated successfully.');
    return true;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Business Information</h2>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={museumInfosEnabled}
              onChange={(e) => handleToggleMuseumTab(e.target.checked)}
              className="w-5 h-5"
            />
            <span className="text-sm font-medium">
              Enable Business Page in App
            </span>
          </label>
        </div>
      </div>

      {/* Linked Museum Objects Catalog */}
      <CollapsibleSection
        icon={Package}
        title="Museum Objects Catalog"
        summary={linkedMuseum ? `Linked: ${linkedMuseum.name}` : 'No museum linked'}
      >
        <p className="text-sm text-purple-700 mb-4">
          Link this guide to a museum's objects catalog. When linked, the guide JSON will include
          <code className="bg-purple-100 px-1 mx-1 rounded text-xs">linkedMuseumId</code>
          allowing the mobile app to display the museum's objects.
        </p>

        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-purple-800">Linked Museum:</label>
          <select
            value={linkedMuseumId}
            onChange={(e) => handleLinkedMuseumChange(e.target.value)}
            disabled={museumsLoading}
            className="flex-1 max-w-md p-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
          >
            <option value="">No linked museum (objects not available)</option>
            {availableMuseums.map(museum => (
              <option key={museum.id} value={museum.id}>
                {museum.name}
              </option>
            ))}
          </select>
        </div>

        {linkedMuseum && (
          <div className="mt-3 p-3 bg-white rounded border border-purple-200">
            <div className="flex items-center gap-2 text-purple-700">
              <Link className="w-4 h-4" />
              <span className="font-medium">Linked to: {linkedMuseum.name}</span>
            </div>
            <p className="text-xs text-purple-600 mt-1">
              Museum ID: <code className="bg-purple-50 px-1 rounded">{linkedMuseum.id}</code>
            </p>
            {linkedMuseum.rooms?.length > 0 && (
              <p className="text-xs text-purple-600 mt-1">
                {linkedMuseum.rooms.length} room(s) configured
              </p>
            )}
          </div>
        )}

        {museumsLoading && (
          <p className="text-sm text-purple-600 mt-2">Loading museums...</p>
        )}

        {!museumsLoading && availableMuseums.length === 0 && (
          <p className="text-sm text-amber-600 mt-2">
            No museums available. Create a museum in the Objects tab first.
          </p>
        )}
      </CollapsibleSection>

      {/* Status Messages */}
      {status === 'success' && (
        <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded border border-green-200">
          <Check className="w-4 h-4" />
          <span>{message}</span>
        </div>
      )}
      {status === 'error' && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded border border-red-200">
          <AlertCircle className="w-4 h-4" />
          <span>{message}</span>
        </div>
      )}

      {!museumInfosEnabled && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-gray-600 text-sm">
            The business page is currently disabled. Enable it above to add business information that will be displayed in the app.
          </p>
        </div>
      )}

      {/* Basic Information */}
      <CollapsibleSection icon={Building2} title="Basic Information" summary="Logo, type, description, hours, admission" defaultOpen>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Business Logo</label>
          <div className="flex flex-col gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-4">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                {museumInfo.logoUrl ? (
                  <img
                    src={museumInfo.logoUrl}
                    alt="Business logo preview"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <Building2 className="h-8 w-8 text-slate-400" />
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isUploading}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                  {isUploading ? 'Uploading...' : 'Upload logo'}
                </button>
                {museumInfo.logoUrl && (
                  <button
                    type="button"
                    onClick={() => handleFieldChange('logoUrl', '')}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>
            {museumInfo.logoUrl && (
              <input
                type="text"
                className="w-full p-2 border rounded text-sm bg-slate-50"
                value={museumInfo.logoUrl}
                onChange={(e) => handleFieldChange('logoUrl', e.target.value)}
                placeholder="https://..."
              />
            )}
            <p className="text-xs text-gray-500">
              This uploads an image and saves the public URL into the business page JSON as the logo.
            </p>
          </div>
        </div>

        {/* Venue Type */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Business Type</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="Business, Museum, Gallery, Restaurant..."
            value={museumInfo.venue_type || ''}
            onChange={(e) => handleFieldChange('venue_type', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Controls the label shown in the app. Leave empty to keep the default business label.
          </p>
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Description {museumInfosEnabled && <span className="text-red-500">*</span>}
          </label>
          <textarea
            className={`w-full p-2 border rounded ${
              museumInfosEnabled && !museumInfo.description ? 'border-yellow-500' : ''
            }`}
            rows={4}
            placeholder="Explore centuries of human innovation and natural wonders..."
            value={museumInfo.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            A brief description of the business. This will be displayed prominently on the business page.
          </p>
        </div>

        {/* Opening Hours */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Opening Hours</label>
          <textarea
            className="w-full p-2 border rounded"
            rows={3}
            placeholder="Mon–Fri: 10 AM – 7 PM&#10;Sat–Sun: 9 AM – 8 PM&#10;Last Entry: 30 mins before closing"
            value={museumInfo.opening_hours || ''}
            onChange={(e) => handleFieldChange('opening_hours', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Use \n for line breaks in JSON. Example: "Mon–Fri: 10 AM – 7 PM\nSat–Sun: 9 AM – 8 PM"
          </p>
        </div>

        {/* Admission */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Admission Prices</label>
          <textarea
            className="w-full p-2 border rounded"
            rows={3}
            placeholder="Adults: $25 • Children/Students: $18&#10;Seniors (65+): $20 • Members: Free"
            value={museumInfo.admission || ''}
            onChange={(e) => handleFieldChange('admission', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Admission prices and categories. Use \n for line breaks.
          </p>
        </div>
      </CollapsibleSection>

      {/* Location Information */}
      <CollapsibleSection icon={MapPin} title="Location Information" summary="Address, coordinates, accessibility">

        {/* Address */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Address</label>
          <textarea
            className="w-full p-2 border rounded"
            rows={2}
            placeholder="1776 Heritage Lane, Metroville&#10;Easy access via Metro Line 3"
            value={museumInfo.address || ''}
            onChange={(e) => handleFieldChange('address', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Full address with directions. Use \n for line breaks.
          </p>
        </div>

        {/* Coordinates */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Latitude</label>
            <input
              type="number"
              step="any"
              className="w-full p-2 border rounded"
              placeholder="42.3601"
              value={museumInfo.latitude || ''}
              onChange={(e) => handleFieldChange('latitude', parseFloat(e.target.value) || '')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Longitude</label>
            <input
              type="number"
              step="any"
              className="w-full p-2 border rounded"
              placeholder="-71.0589"
              value={museumInfo.longitude || ''}
              onChange={(e) => handleFieldChange('longitude', parseFloat(e.target.value) || '')}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          If latitude and longitude are provided, the address becomes clickable and opens in maps.
        </p>

        {/* Accessibility */}
        <div>
          <label className="block text-sm font-medium mb-1">Accessibility</label>
          <textarea
            className="w-full p-2 border rounded"
            rows={2}
            placeholder="Fully wheelchair accessible facilities with elevators and ramps."
            value={museumInfo.accessibility || ''}
            onChange={(e) => handleFieldChange('accessibility', e.target.value)}
          />
        </div>
      </CollapsibleSection>

      {/* Facilities & Services */}
      <CollapsibleSection icon={Coffee} title="Facilities & Services" summary="WiFi, gift shop, cafe">

        {/* WiFi */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 flex items-center gap-2">
            <Wifi className="w-4 h-4" />
            WiFi Information
          </label>
          <textarea
            className="w-full p-2 border rounded"
            rows={2}
            placeholder="Network: DISCOVER_NET&#10;High-speed connection throughout."
            value={museumInfo.wifi || ''}
            onChange={(e) => handleFieldChange('wifi', e.target.value)}
          />
        </div>

        {/* Gift Shop */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" />
            Gift Shop
          </label>
          <textarea
            className="w-full p-2 border rounded"
            rows={2}
            placeholder="Find unique souvenirs, books, and educational gifts."
            value={museumInfo.gift_shop || ''}
            onChange={(e) => handleFieldChange('gift_shop', e.target.value)}
          />
        </div>

        {/* Cafe */}
        <div>
          <label className="block text-sm font-medium mb-1 flex items-center gap-2">
            <Coffee className="w-4 h-4" />
            Cafe / Restaurant
          </label>
          <textarea
            className="w-full p-2 border rounded"
            rows={2}
            placeholder="Open daily until 6 PM&#10;Light meals, snacks, and beverages."
            value={museumInfo.cafe || ''}
            onChange={(e) => handleFieldChange('cafe', e.target.value)}
          />
        </div>
      </CollapsibleSection>

      {/* Contact Information */}
      <CollapsibleSection icon={Phone} title="Contact Information" summary="Phone, email, website">

        {/* Phone */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Phone
          </label>
          <textarea
            className="w-full p-2 border rounded"
            rows={2}
            placeholder="+1 (555) 123-4567&#10;Available Mon-Fri, 9 AM - 5 PM"
            value={museumInfo.phone || ''}
            onChange={(e) => handleFieldChange('phone', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Phone numbers are automatically cleaned for dialing and become tappable.
          </p>
        </div>

        {/* Email */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email
          </label>
          <textarea
            className="w-full p-2 border rounded"
            rows={2}
            placeholder="info@themuseum.org&#10;We typically respond within 24 hours."
            value={museumInfo.email || ''}
            onChange={(e) => handleFieldChange('email', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Email addresses become tappable and open the default email app.
          </p>
        </div>

        {/* Website */}
        <div>
          <label className="block text-sm font-medium mb-1 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Website
          </label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="www.themuseum.org"
            value={museumInfo.website || ''}
            onChange={(e) => handleFieldChange('website', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Website URL. "https://" will be added automatically if missing. Becomes tappable.
          </p>
        </div>
      </CollapsibleSection>

      {/* Validation Button */}
      <div className="flex justify-between items-center pt-4 border-t">
        <button
          onClick={validateFields}
          className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 text-sm"
        >
          Validate Business Information
        </button>

        {museumInfosEnabled && (
          <p className="text-xs text-gray-600">
            All fields are optional except description. Empty fields won't be displayed in the app.
          </p>
        )}
      </div>
    </div>
  );
};
